/**
 * Phase F4 — Admin Support Center
 * --------------------------------
 * Surfaces Phase 10 support tickets.
 *
 * Endpoints used:
 *   GET  /api/admin/support/tickets?status=…
 *   POST /api/admin/support/tickets/{ticket_id}/transition (body: {target, notes?})
 *   POST /api/admin/support/tickets/{ticket_id}/assign     (body: {admin_id})
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LifeBuoy, Clock, Eye, UserCheck, CheckCircle, Archive, X, MessageSquare,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'OPEN', label: 'Open Tickets', Icon: Clock, color: theme.colors.status.warning },
  { key: 'IN_REVIEW', label: 'In Review', Icon: Eye, color: theme.colors.status.info },
  { key: 'WAITING_FOR_USER', label: 'Waiting For User', Icon: UserCheck, color: theme.colors.accent.teal },
  { key: 'RESOLVED', label: 'Resolved', Icon: CheckCircle, color: theme.colors.status.success },
  { key: 'CLOSED', label: 'Closed', Icon: Archive, color: theme.colors.text.muted },
];

const ALLOWED_NEXT = {
  OPEN: ['IN_REVIEW', 'RESOLVED', 'CLOSED'],
  IN_REVIEW: ['WAITING_FOR_USER', 'RESOLVED', 'OPEN', 'CLOSED'],
  WAITING_FOR_USER: ['IN_REVIEW', 'RESOLVED', 'CLOSED'],
  RESOLVED: ['IN_REVIEW', 'CLOSED'],
  CLOSED: [],
};

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminSupportCenter({ user }) {
  const [activeTab, setActiveTab] = useState('OPEN');
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]); // for counts
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, filtered] = await Promise.all([
        axios.get(`${API}/admin/support/tickets`, auth),
        axios.get(`${API}/admin/support/tickets?status=${activeTab}`, auth),
      ]);
      setAllRows(all.data?.tickets || []);
      setRows(filtered.data?.tickets || []);
    } catch (e) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [activeTab, auth]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => { c[t.key] = 0; });
    allRows.forEach((t) => { if (c[t.status] !== undefined) c[t.status] += 1; });
    return c;
  }, [allRows]);

  const transition = async (id, target) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/support/tickets/${id}/transition`, { target, notes: notes || null }, auth);
      toast.success(`Moved to ${target}`);
      setNotes('');
      setDrawer(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transition failed');
    } finally { setBusy(false); }
  };

  const assignToMe = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/support/tickets/${id}/assign`, { admin_id: user?.id }, auth);
      toast.success('Assigned to you');
      await load();
    } catch (e) {
      toast.error('Assign failed');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-support-center-page" style={{ padding: 24, maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <LifeBuoy size={26} color={theme.colors.primary} />
          Support Center
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Tickets across the platform — assign, transition, and add notes.
        </p>
      </div>

      <div data-testid="support-tabs" style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.Icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`support-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: theme.radius.cta,
                border: `1px solid ${active ? t.color : theme.colors.borders}`,
                background: active ? `${t.color}15` : theme.colors.bg.card,
                color: active ? t.color : theme.colors.text.secondary,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              <Icon size={16} /> {t.label}
              <span style={{
                marginLeft: 4, background: active ? t.color : theme.colors.bg.tertiary,
                color: active ? '#fff' : theme.colors.text.secondary,
                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              }}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div data-testid="support-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="support-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          No tickets in this bucket.
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Ticket #', 'Subject', 'Role', 'Status', 'Assigned', 'Updated', ''].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} data-testid={`ticket-row-${t.id}`} style={{ borderTop: `1px solid ${theme.colors.borders}` }}>
                  <td style={{ padding: '10px 12px', fontWeight: 700 }}>{t.ticket_number || t.id?.slice(0, 8)}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: theme.colors.text.primary }}>{t.subject || '—'}</td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{t.role}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999,
                      background: `${theme.colors.primary}15`, color: theme.colors.primary,
                      fontSize: 11, fontWeight: 700,
                    }}>{t.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: theme.colors.text.secondary }}>
                    {t.assigned_admin_email || '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{fmtDate(t.updated_at)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {!t.assigned_admin_id && (
                        <button
                          data-testid={`ticket-assign-${t.id}`} disabled={busy}
                          onClick={() => assignToMe(t.id)}
                          style={{
                            padding: '5px 10px', borderRadius: theme.radius.sm,
                            border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
                            cursor: 'pointer', fontSize: 11, fontWeight: 700, color: theme.colors.primary,
                          }}>Assign to me</button>
                      )}
                      <button
                        data-testid={`ticket-open-${t.id}`}
                        onClick={() => { setDrawer(t); setNotes(''); }}
                        style={{
                          padding: '5px 10px', borderRadius: theme.radius.sm, cursor: 'pointer',
                          border: 'none', background: theme.colors.primary, color: '#fff',
                          fontSize: 11, fontWeight: 700,
                        }}>Open</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawer && (
        <div data-testid="ticket-drawer" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', justifyContent: 'flex-end', zIndex: 100,
        }} onClick={() => setDrawer(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: 'min(520px, 100%)', height: '100%', background: theme.colors.bg.card,
            padding: 24, overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>{drawer.ticket_number || drawer.id?.slice(0, 8)}</div>
              <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ marginBottom: 12, fontWeight: 700, color: theme.colors.text.primary }}>
              {drawer.subject}
            </div>
            <div style={{ marginBottom: 12, fontSize: 13, color: theme.colors.text.secondary }}>
              {drawer.description || drawer.details || 'No description.'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 14, fontSize: 13 }}>
              {[
                ['Status', drawer.status], ['Role', drawer.role],
                ['User', drawer.user_id?.slice(0, 8) + '…'],
                ['Updated', fmtDate(drawer.updated_at)],
                ['Assigned', drawer.assigned_admin_email || '—'],
              ].map(([k, v]) => (
                <div key={k} style={{ padding: 6, background: theme.colors.bg.secondary, borderRadius: theme.radius.sm }}>
                  <div style={{ fontSize: 10, color: theme.colors.text.muted, textTransform: 'uppercase' }}>{k}</div>
                  <div style={{ fontWeight: 600 }}>{v || '—'}</div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary, marginBottom: 6, display: 'block' }}>
                <MessageSquare size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Notes
              </label>
              <textarea
                data-testid="ticket-notes"
                value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes for this transition…"
                style={{
                  width: '100%', minHeight: 70, padding: 10,
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm, fontFamily: 'inherit', fontSize: 13,
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(ALLOWED_NEXT[drawer.status] || []).map((target) => (
                <button
                  key={target}
                  data-testid={`ticket-transition-${target}`}
                  disabled={busy}
                  onClick={() => transition(drawer.id, target)}
                  style={{
                    padding: '7px 12px', borderRadius: theme.radius.sm, cursor: 'pointer',
                    border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
                    fontSize: 12, fontWeight: 700, color: theme.colors.text.primary,
                  }}
                >
                  → {target.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
